import React from 'react'
import { DateTimeV2, Truncate, VerticalDivider } from '@labrav/react-components'
import { CloudMarketplace } from '@labrav/react-components/lib/@types/types/forms'
import { ResaleAuthorizationStatus } from '../../../common/components/StatusChips'
import { OfferStatus as OfferStatusEnum } from '../../../modules/privateOffer/reducer'
import { useStyles } from './ResellerAuthorizationCard.styles'
import { CardBox } from '../CardBox/CardBox'
import { getCurrencySymbol } from '@labrav/common'
import {
  DiscountType,
  type ResaleAuthorizationsResellerAgreements,
  ResellerAgreementStatus,
} from '../../../admin/modules/resaleAuthorizations/reducer'

export interface ResellerAuthorizationCardProps {
  resellerAuthorization: ResaleAuthorizationsResellerAgreements
  index: number
  isCollapsed: boolean
  onToggleCollapse: () => void
  onResellerAuthorizationClick?: (
    resellerAuthorizationId: string,
    productId: string,
    draft: boolean,
    cloudAccountId?: string,
    cloudMarketplace?: CloudMarketplace
  ) => void
}

export const ResellerAuthorizationCard: React.FC<
  ResellerAuthorizationCardProps
> = ({
  resellerAuthorization,
  index,
  isCollapsed,
  onToggleCollapse,
  onResellerAuthorizationClick,
}) => {
  const { classes } = useStyles()
  const resellerAuthorizationId = resellerAuthorization.id
  const cloudType = resellerAuthorization.cloud

  const expiryDate = resellerAuthorization.agreementExpirationDate
  const isExpired = expiryDate && new Date(expiryDate) < new Date()

  const productId =
    cloudType === 'AWS'
      ? resellerAuthorization.awsProductId ||
        resellerAuthorization.products?.[0]?.awsProductId ||
        ''
      : cloudType === 'AZURE'
        ? resellerAuthorization.labraProductId || ''
        : ''

  const isDraft =
    cloudType === 'AWS'
      ? !!resellerAuthorization.draft
      : cloudType === 'AZURE'
        ? resellerAuthorization.offerStatus ===
          ResellerAgreementStatus.OFFER_IN_DRAFT
        : !!(
            resellerAuthorization.draft ||
            resellerAuthorization.offerStatus ===
              ResellerAgreementStatus.OFFER_IN_DRAFT ||
            resellerAuthorization.status === ResellerAgreementStatus.DRAFT
          )

  const handleResellerAuthorizationClick = (
    resellerAuthorizationId: string
  ) => {
    if (onResellerAuthorizationClick && resellerAuthorizationId && productId) {
      const cloudMarketplace = resellerAuthorization.cloud as CloudMarketplace
      onResellerAuthorizationClick(
        resellerAuthorizationId,
        productId,
        isDraft,
        resellerAuthorization.cloudAccountId,
        cloudMarketplace || ''
      )
    }
  }

  const tcv = getTcvValue(resellerAuthorization)

  const isTcvAvailable =
    resellerAuthorization.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS

  const currencySymbol = getCurrencySymbol(
    resellerAuthorization?.currency || 'USD'
  )
  return (
    <CardBox
      id={resellerAuthorizationId}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
      onCardClick={() =>
        handleResellerAuthorizationClick(resellerAuthorizationId)
      }
      copyLinkUrl={
        isDraft
          ? undefined
          : cloudType === 'AZURE'
            ? resellerAuthorization.acceptanceLink
            : resellerAuthorization.labraOfferUrl
      }
      cloudType={cloudType}
      title={resellerAuthorization.agreementName || 'Reseller Authorization'}
      viewLink={!isDraft}
      content={
        <>
          <div className={classes.cardGrid}>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>Company name</div>
              <div className={classes.metaValue}>
                <Truncate width={200} clipboardTooltip>
                  {resellerAuthorization.companyName || '-'}
                </Truncate>
              </div>
            </div>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>Reseller name</div>
              <div className={classes.metaValue}>
                <Truncate width={200} clipboardTooltip>
                  {resellerAuthorization.resellersInfo?.[0]?.companyName || '-'}
                </Truncate>
              </div>
            </div>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>Authorization ID</div>
              <div className={classes.metaValue}>
                <Truncate width={200} clipboardTooltip>
                  {resellerAuthorization.resellerAuthorizationId || '-'}
                </Truncate>
              </div>
            </div>
            <div className={classes.gridItem}>
              <div className={classes.metaLabel}>Expiry date</div>
              <div
                className={`${classes.metaValue} ${classes.expiryDateValue}`}
              >
                {expiryDate ? (
                  <>
                    <DateTimeV2 variant="formattedOnly" date={expiryDate} />
                    {isExpired && (
                      <span className={classes.expiredText}>(Expired)</span>
                    )}
                  </>
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
        </>
      }
      headerContent={
        <>
          <div className={classes.headerContent}>
            {(resellerAuthorization.status ||
              resellerAuthorization.offerStatus ||
              isDraft) && (
              <div className={classes.statusRow}>
                <div className={classes.statusItem}>
                  <ResaleAuthorizationStatus
                    status={
                      isDraft
                        ? OfferStatusEnum.OFFER_IN_DRAFT
                        : resellerAuthorization.offerStatus ||
                          resellerAuthorization.status
                    }
                  />
                </div>
              </div>
            )}
            {!!isTcvAvailable && (
              <>
                <VerticalDivider height="24px" />
                <div className={classes.tcvText}>
                  <span className={classes.tcvLabel}>
                    TCV: {currencySymbol}
                  </span>
                  <span className={classes.tcvLabel}>{tcv}</span>
                </div>
              </>
            )}
          </div>
        </>
      }
    />
  )
}

const getTcvValue = (
  resellerAuthorization: ResaleAuthorizationsResellerAgreements
) => {
  if (
    resellerAuthorization.discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS
  ) {
    return resellerAuthorization.installmentInfo?.reduce(
      (acc: number, installment: { amount?: number | null }) => {
        return acc + (installment.amount || 0)
      },
      0
    )
  }

  return 0
}
